import { RefObject } from 'react';
import type { Options } from './_common';

const DIRECTIONS = {
	horizontal: ['ArrowRight', 'ArrowLeft'] as const,
	vertical: ['ArrowDown', 'ArrowUp'] as const,
} as const;

export function handleEvents<T extends HTMLElement>(event: KeyboardEvent, ref: RefObject<T>, options: Options): void {
	const { selectors, direction = 'vertical', releaseLockAtWrap = 'end' } = options;

	const parentNode = ref.current;
	if (parentNode == null) return;

	const [forward, backward] = DIRECTIONS[direction];

	const key = event.key;
	if (![forward, backward, 'Enter'].includes(key)) return;

	const activeElement = document.activeElement as HTMLDivElement;
	if (activeElement == null) return;

	// If we're not inside the container, don't do anything
	if (!parentNode.contains(activeElement)) return;

	// Get the list of elements we're allowed to scroll through
	const availableElements = parentNode.querySelectorAll<HTMLElement>(selectors.join(','));
	if (!availableElements.length) return;

	// Which index is currently selected
	const currentIndex = Array.from(availableElements).findIndex((availableElement) => availableElement === activeElement);

	if (key === 'Enter') {
		if (currentIndex === -1) return;

		activeElement.click();
		event.preventDefault();

		return;
	}

	// If the focus isn't in the container, focus on the first thing
	if (currentIndex === -1) {
		availableElements[0].focus();
	}

	// Move the focus up or down
	let nextElement: HTMLElement | undefined;
	if (event.key === forward) {
		let nextIndex = currentIndex + 1;
		if (nextIndex === availableElements.length) {
			if (releaseLockAtWrap === 'start' || releaseLockAtWrap === 'both') {
				event.preventDefault();
				availableElements[currentIndex].blur();

				return;
			}

			nextIndex = 0;
		}

		nextElement = availableElements[nextIndex];
	} else if (event.key === backward) {
		let nextIndex = currentIndex - 1;
		if (nextIndex === -1) {
			if (releaseLockAtWrap === 'end' || releaseLockAtWrap === 'both') {
				event.preventDefault();
				availableElements[currentIndex].blur();

				return;
			}

			nextIndex = availableElements.length - 1;
		}

		nextElement = availableElements[nextIndex];
	} else {
		return;
	}

	nextElement.focus();
	event.preventDefault();
}
